import { useState, useRef, useCallback, useEffect } from 'react';
import { Camera, Images, X, Crop, Loader2, Search } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { SearchInput } from '@/components/ui/search-input';
import { cn } from '@/lib/utils';
import { formatGradingCompany } from '@/constants/gradingCompanies';
import { UserItem } from '@/types/userItem';
import { useUploadItemImages } from '@/hooks/useUserItems';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/lib/toast';
import { ItemImageEditorDialog } from './ItemImageEditorDialog';
import { getGameLabel } from '@/constants/cardGames';

interface StockImageOption {
  imageUrl: string;
  sourceUrl: string;
  score: number;
  condition?: string;
  price?: string;
}

interface DebugInfo {
  cleanTitle: string;
  setIdentifier: string;
  gamePrefix: string;
  queryTiers: string[];
  usedTier?: string;
}

interface RateLimitInfo {

  limit: number;
  remaining: number;
  resetAt: string;
}

interface ImageSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  item: UserItem;
  hasExistingImages?: boolean;
  /** When provided, stock image selection returns URL via callback instead of saving to DB */
  onStockImageSelect?: (imageUrl: string) => void;
  /** Default tab to show when dialog opens */
  defaultTab?: 'upload' | 'stock';
  /** Hide the upload tab entirely and show only the stock picker */
  stockOnly?: boolean;
}

// Build a descriptive label for display (title + set + grade)
function buildItemDisplayLabel(item: UserItem): string {
  const parts: string[] = [item.title];
  
  // Add set identifier (prefer set_code, fallback to set_name)
  const setIdentifier = (item.set_code || item.set_name || '').trim();
  if (setIdentifier && !item.title.toLowerCase().includes(setIdentifier.toLowerCase())) {
    parts.push(setIdentifier);
  }
  
  // For slabs, add grading company and grade
  if (item.category === 'slabs' && item.grading_company) {
    let gradeInfo = formatGradingCompany(item.grading_company);
    if (item.slab_grade) {
      gradeInfo += ` ${item.slab_grade}`;
    }
    parts.push(gradeInfo);
  }
  
  return parts.join(' ');
}

// Build search query with title + set name + game name so user sees the full query
function buildDefaultSearchQuery(item: UserItem): string {
  // Expand language abbreviations in title
  let query = item.title
    .replace(/\(CN\)/gi, 'Chinese')
    .replace(/\(JP\)/gi, 'Japanese')
    .replace(/\(KR\)/gi, 'Korean')
    .replace(/\(EN\)/gi, 'English')
    .trim();

  const queryLower = query.toLowerCase();

  // Always include set name if available - so user sees the full query
  const setIdentifier = (item.set_code || item.set_name || '').trim();
  if (setIdentifier) {
    // Only add if not already in the title (case-insensitive check)
    const setLower = setIdentifier.toLowerCase();
    if (!queryLower.includes(setLower)) {
      query = `${query} ${setIdentifier}`;
    }
  }

  // Include game name if available and not already in the title
  const gameName = item.custom_game_name?.trim() || getGameLabel(item.game);
  if (gameName && gameName !== 'Unknown') {
    const gameLower = gameName.toLowerCase();
    if (!queryLower.includes(gameLower)) {
      query = `${query} ${gameName}`;
    }
  }

  return query.trim();
}

async function fetchStockImageOptions(
  searchQuery: string, 
  item: UserItem,
  offset: number = 0,
  excludeUrls: string[] = [],
  dryRun: boolean = false
): Promise<{ 
  options: StockImageOption[]; 
  found: boolean;
  hasMore: boolean;
  nextOffset: number;
  debug?: DebugInfo;
  rateLimit?: RateLimitInfo;
  error?: string;
}> {
  const { data, error } = await supabase.functions.invoke('fetch-stock-image', {
    body: {
      title: searchQuery,
      category: item.category,
      set_code: item.set_code,
      set_name: item.set_name,
      card_number: item.card_number,
      grading_company: item.grading_company,
      slab_grade: item.slab_grade,
      product_type: item.product_type,
      game: item.game,
      rarity: item.rarity,
      returnMultiple: true,
      offset,
      excludeUrls,
      debug: true,
      dryRun,
    },
  });

  if (error) {
    console.error('Error fetching stock images:', error);
    // Try to parse error body for rate limit info
    try {
      const errorData = typeof error === 'object' && 'context' in error ? error.context : null;
      if (errorData && errorData.rateLimit) {
        return { 
          options: [], 
          found: false, 
          hasMore: false, 
          nextOffset: offset,
          debug: errorData.debug,
          rateLimit: errorData.rateLimit,
          error: 'Rate limit exceeded',
        };
      }
    } catch {
      // Ignore parse errors
    }
    return { options: [], found: false, hasMore: false, nextOffset: offset, error: 'Search failed' };
  }
  
  // Check if response itself contains rate limit error
  if (data?.error === 'Rate limit exceeded') {
    return {
      options: [],
      found: false,
      hasMore: false,
      nextOffset: offset,
      debug: data.debug,
      rateLimit: data.rateLimit,
      error: 'Rate limit exceeded',
    };
  }

  return {
    options: data?.options || [],
    found: data?.found ?? false,
    hasMore: data?.hasMore === true,
    nextOffset: data?.nextOffset ?? offset + 48,
    debug: data?.debug,
  };
}

export function ImageSelectionDialog({ 
  open, 
  onClose, 
  item, 
  hasExistingImages = false,
  onStockImageSelect,
  defaultTab = 'upload',
  stockOnly = false,
}: ImageSelectionDialogProps) {
  const initialTab = stockOnly ? 'stock' : defaultTab;
  const [activeTab, setActiveTab] = useState<'upload' | 'stock'>(initialTab);
  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSavingStock, setIsSavingStock] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [stockOptions, setStockOptions] = useState<StockImageOption[]>([]);
  const [isLoadingStock, setIsLoadingStock] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [stockImageToEdit, setStockImageToEdit] = useState<string | null>(null);
  
  // Pagination state
  const [seenUrls, setSeenUrls] = useState<Set<string>>(new Set());
  const [hasMore, setHasMore] = useState(false);
  const [nextOffset, setNextOffset] = useState(0);
  
  // Rate limit info state
  const [rateLimitInfo, setRateLimitInfo] = useState<RateLimitInfo | null>(null);
  
  const queryClient = useQueryClient();
  const uploadImages = useUploadItemImages();

  // Initialize search query when dialog opens or tab switches to stock
  useEffect(() => {
    if (open && activeTab === 'stock' && !hasSearched) {
      const defaultQuery = buildDefaultSearchQuery(item);
      setSearchQuery(defaultQuery);
      // Auto-search on first open
      handleStockSearch(defaultQuery);
    }
  }, [open, activeTab, item.id]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setSearchQuery('');
      setStockOptions([]);
      setHasSearched(false);
      setStockImageToEdit(null);
      setSeenUrls(new Set());
      setHasMore(false);
      setNextOffset(0);
      setRateLimitInfo(null);
    }
  }, [open]);

  // Debounced auto-search as user types (skip the initial mount + first auto-search)
  useEffect(() => {
    if (!open || activeTab !== 'stock' || !hasSearched) return;
    const term = searchQuery.trim();
    if (term.length < 2) return;
    const t = setTimeout(() => {
      handleStockSearch(term);
    }, 500);
    return () => clearTimeout(t);
  }, [searchQuery, open, activeTab, hasSearched]);

  const handleStockSearch = async (query?: string) => {
    const searchTerm = (query || searchQuery).trim();
    if (!searchTerm || searchTerm.length < 2) {
      toast.error('Please enter at least 2 characters');
      return;
    }
    
    // Basic input validation - max 200 chars, no script tags
    if (searchTerm.length > 200) {
      toast.error('Search query too long');
      return;
    }
    
    setIsLoadingStock(true);
    setHasSearched(true);
    // Reset pagination on new search
    setSeenUrls(new Set());
    setHasMore(false);
    setNextOffset(0);
    setRateLimitInfo(null);
    
    try {
      const result = await fetchStockImageOptions(searchTerm, item, 0, []);
      
      // Note: intentionally do NOT sync result.debug.usedTier back into the
      // visible search input — it contains internal operators like "-opened"
      // that confuse users and would re-trigger the debounced auto-search.
      
      // Handle rate limit error
      if (result.error === 'Rate limit exceeded' && result.rateLimit) {
        setRateLimitInfo(result.rateLimit);
        const resetTime = new Date(result.rateLimit.resetAt);
        toast.error(`Rate limited. Resets at ${resetTime.toLocaleTimeString()}`);
        return;
      }
      
      setStockOptions(result.options);
      setSeenUrls(new Set(result.options.map(o => o.imageUrl)));
      setHasMore(result.hasMore);
      setNextOffset(result.nextOffset);
    } catch (error) {
      console.error('Stock search error:', error);
      toast.error('Failed to search for images');
    } finally {
      setIsLoadingStock(false);
    }
  };

  const handleLoadMore = async () => {
    if (!hasMore || isLoadingMore) return;
    
    setIsLoadingMore(true);
    try {
      const result = await fetchStockImageOptions(
        searchQuery.trim(), 
        item, 
        nextOffset, 
        Array.from(seenUrls)
      );
      
      if (result.options.length > 0) {
        // Filter out any duplicates that might have slipped through
        const newOptions = result.options.filter(o => !seenUrls.has(o.imageUrl));
        if (newOptions.length > 0) {
          setStockOptions(prev => [...prev, ...newOptions]);
          setSeenUrls(prev => {
            const updated = new Set(prev);
            newOptions.forEach(o => updated.add(o.imageUrl));
            return updated;
          });
        }
      }
      
      setHasMore(result.hasMore);
      setNextOffset(result.nextOffset);
    } catch (error) {
      console.error('Load more error:', error);
      toast.error('Failed to load more images');
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleFilesSelect = useCallback(async (selectedFiles: FileList | File[]) => {
    const selectedFileArray = Array.from(selectedFiles as ArrayLike<File>);
    const { processSelectedFiles } = await import('@/lib/photoInputUtils');
    const fileArray = await processSelectedFiles(selectedFileArray, { maxCount: 10 });
    if (fileArray.length === 0) return;

    // Read all previews in parallel but preserve selection order, so preview[i]
    // always matches file[i]. Previously we pushed previews as each FileReader
    // resolved, which raced with selection order — the second-selected photo
    // often finished decoding first and became the "hero", and deleting a
    // preview removed the wrong underlying file.
    const dataUrls = await Promise.all(
      fileArray.map(
        (file) =>
          new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(file);
          }),
      ),
    );

    setFiles((prev) => [...prev, ...fileArray]);
    setPreviews((prev) => [...prev, ...dataUrls]);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files) {
      handleFilesSelect(e.dataTransfer.files);
    }
  }, [handleFilesSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleRemoveFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const [touchDragActive, setTouchDragActive] = useState(false);
  const longPressTimerRef = useRef<number | null>(null);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);

  const reorderArr = <T,>(arr: T[], from: number, to: number): T[] => {
    const next = arr.slice();
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    return next;
  };

  const commitReorder = (from: number, to: number) => {
    if (from === to) return;
    setFiles(prev => reorderArr(prev, from, to));
    setPreviews(prev => reorderArr(prev, from, to));
  };

  const handleTileDrop = (toIndex: number) => {
    if (dragIndex === null || dragIndex === toIndex) {
      setDragIndex(null);
      setOverIndex(null);
      return;
    }
    commitReorder(dragIndex, toIndex);
    setDragIndex(null);
    setOverIndex(null);
  };

  const clearLongPress = () => {
    if (longPressTimerRef.current !== null) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handleTilePointerDown = (index: number) => (e: React.PointerEvent) => {
    if (e.pointerType !== 'touch') return;
    touchStartRef.current = { x: e.clientX, y: e.clientY };
    clearLongPress();
    longPressTimerRef.current = window.setTimeout(() => {
      setDragIndex(index);
      setTouchDragActive(true);
      try { navigator.vibrate?.(15); } catch {}
    }, 350);
  };

  const handleTilePointerMove = (e: React.PointerEvent) => {
    if (e.pointerType !== 'touch') return;
    // Cancel long-press if the finger drifts before activation
    if (!touchDragActive && touchStartRef.current) {
      const dx = e.clientX - touchStartRef.current.x;
      const dy = e.clientY - touchStartRef.current.y;
      if (Math.hypot(dx, dy) > 8) clearLongPress();
      return;
    }
    if (!touchDragActive) return;
    e.preventDefault();
    const el = document.elementFromPoint(e.clientX, e.clientY);
    const tile = el?.closest('[data-tile-index]') as HTMLElement | null;
    if (tile) {
      const idx = Number(tile.dataset.tileIndex);
      if (!Number.isNaN(idx) && idx !== overIndex) setOverIndex(idx);
    }
  };

  const handleTilePointerUp = (e: React.PointerEvent) => {
    if (e.pointerType !== 'touch') return;
    clearLongPress();
    if (touchDragActive && dragIndex !== null && overIndex !== null) {
      commitReorder(dragIndex, overIndex);
    }
    setTouchDragActive(false);
    setDragIndex(null);
    setOverIndex(null);
    touchStartRef.current = null;
  };

  const handleCropSave = (blob: Blob) => {
    if (editingIndex === null) return;
    
    const newFile = new File([blob], files[editingIndex].name, { type: 'image/jpeg' });
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviews(prev => {
        const updated = [...prev];
        updated[editingIndex] = reader.result as string;
        return updated;
      });
    };
    reader.readAsDataURL(blob);
    
    setFiles(prev => {
      const updated = [...prev];
      updated[editingIndex] = newFile;
      return updated;
    });
    setEditingIndex(null);
  };

  const handleUpload = async () => {
    if (files.length === 0) return;
    
    setIsUploading(true);
    try {
      await uploadImages.mutateAsync({ itemId: item.id, files });
      await queryClient.invalidateQueries({ queryKey: ['my-items'] });
      await queryClient.invalidateQueries({ queryKey: ['user-item', item.id] });
      toast.success(`${files.length} photo${files.length > 1 ? 's' : ''} uploaded`);
      // Clear the staged files but keep the modal open so the user can
      // continue managing the item (add more photos, pick a stock image,
      // review, etc). They dismiss explicitly with the close button.
      setFiles([]);
      setPreviews([]);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload photos');
    } finally {
      setIsUploading(false);
    }
  };

  // Open crop editor for stock image instead of saving directly
  const handleStockImageClick = (imageUrl: string) => {
    // If callback mode (add form), just return the raw URL
    if (onStockImageSelect) {
      onStockImageSelect(imageUrl);
      handleClose();
      return;
    }
    setStockImageToEdit(imageUrl);
  };

  // Save cropped stock image to stock_image_url (keeps "Stock" badge)
  const handleStockCropSave = async (blob: Blob) => {
    setStockImageToEdit(null);
    setIsSavingStock(true);
    try {
      // Get current user for storage path
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Upload to storage with user-prefixed path for RLS
      const fileName = `stock-${Date.now()}.jpg`;
      const filePath = `${user.id}/${item.id}/${fileName}`;
      
      const { error: uploadError } = await supabase.storage
        .from('listing-images')
        .upload(filePath, blob, { contentType: 'image/jpeg' });
      
      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('listing-images')
        .getPublicUrl(filePath);

      // Save to stock_image_url (not user_item_images) to preserve "Stock" badge
      const { error: updateError } = await supabase
        .from('user_items')
        .update({ stock_image_url: urlData.publicUrl })
        .eq('id', item.id);

      if (updateError) throw updateError;

      await queryClient.invalidateQueries({ queryKey: ['my-items'] });
      await queryClient.invalidateQueries({ queryKey: ['user-item', item.id] });
      toast.success('Stock image saved');
      handleClose();
    } catch (error) {
      console.error('Error saving cropped stock image:', error);
      toast.error('Failed to save image');
    } finally {
      setIsSavingStock(false);
    }
  };

  const handleClose = () => {
    setFiles([]);
    setPreviews([]);
    setActiveTab(initialTab);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
        <DialogContent
          hideCloseButton={stockOnly}
          className={cn(
            'sm:max-w-2xl flex flex-col',
            stockOnly &&
              'sm:w-[42rem] sm:max-w-[calc(100vw-2rem)] sm:h-[520px] max-sm:p-0 max-sm:gap-0 max-sm:!fixed max-sm:!inset-0 max-sm:!w-screen max-sm:!h-[100dvh] max-sm:!max-w-none max-sm:!translate-x-0 max-sm:!translate-y-0 max-sm:!top-0 max-sm:!left-0 max-sm:!rounded-none max-sm:border-0'
          )}
        >
          {stockOnly ? (
            <>
              <DialogTitle className="sr-only">Sample Photos</DialogTitle>
              <button
                type="button"
                aria-label="Close dialog"
                onClick={handleClose}
                className="absolute right-2 top-2 z-20 hidden h-9 w-9 items-center justify-center rounded-full text-white/70 transition-colors hover:bg-white/[0.06] hover:text-white focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30 sm:flex"
              >
                <X className="h-5 w-5" />
              </button>
            </>
          ) : (
            <DialogHeader className="pb-2">
              <DialogTitle className="text-base font-medium truncate pr-10">
                {`Add Photo to "${buildItemDisplayLabel(item)}"`}
              </DialogTitle>
            </DialogHeader>
          )}

          <Tabs
            value={activeTab}
            onValueChange={(v) => setActiveTab(v as 'upload' | 'stock')}
            className="flex-1 min-h-0 flex flex-col"
          >
            {!stockOnly && (
              <TabsList className="w-full">
                <TabsTrigger value="upload" className="flex-1 gap-1.5">
                  <Camera className="w-4 h-4" />
                  Upload Photo
                </TabsTrigger>
                <TabsTrigger value="stock" className="flex-1 gap-1.5">
                  <Images className="w-4 h-4" />
                  Sample Photos
                </TabsTrigger>
              </TabsList>
            )}

            {/* Upload Tab */}
            {!stockOnly && (
            <TabsContent value="upload" className="mt-4 flex-1 min-h-0 overflow-y-auto">
              {previews.length === 0 ? (
                <div
                  className={cn(
                    "border border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors",
                    isDragging 
                      ? "border-primary bg-primary/10" 
                      : "border-white/20 hover:border-white/40 hover:bg-white/5"
                  )}
                  onClick={() => fileInputRef.current?.click()}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                >
                  <Camera className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground mb-1">
                    Click to upload or drag & drop
                  </p>
                  <p className="text-xs text-muted-foreground/60">
                    JPG, PNG up to 10MB each
                  </p>
                  <p className="text-[11px] text-muted-foreground/70 mt-3 max-w-[260px] mx-auto leading-snug">
                    Tip: include a small note with your @username and today's date in the photo so buyers know it's recent.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-2">
                    {previews.map((preview, index) => (
                      <div
                        key={index}
                        data-tile-index={index}
                        draggable
                        onDragStart={(e) => {
                          setDragIndex(index);
                          e.dataTransfer.effectAllowed = 'move';
                          try { e.dataTransfer.setData('text/plain', String(index)); } catch {}
                        }}
                        onDragOver={(e) => {
                          if (dragIndex === null) return;
                          e.preventDefault();
                          e.dataTransfer.dropEffect = 'move';
                          if (overIndex !== index) setOverIndex(index);
                        }}
                        onDragLeave={() => {
                          if (overIndex === index) setOverIndex(null);
                        }}
                        onDrop={(e) => {
                          if (dragIndex === null) return;
                          e.preventDefault();
                          e.stopPropagation();
                          handleTileDrop(index);
                        }}
                        onDragEnd={() => {
                          setDragIndex(null);
                          setOverIndex(null);
                        }}
                        onPointerDown={handleTilePointerDown(index)}
                        onPointerMove={handleTilePointerMove}
                        onPointerUp={handleTilePointerUp}
                        onPointerCancel={handleTilePointerUp}
                        style={touchDragActive ? { touchAction: 'none' } : undefined}
                        className={cn(
                          "group relative aspect-square rounded-lg overflow-hidden bg-black/40 border border-white/10 cursor-move transition-all select-none",
                          dragIndex === index && "opacity-40 scale-95",
                          overIndex === index && dragIndex !== null && dragIndex !== index && "ring-2 ring-active-gold border-active-gold"
                        )}
                      >
                        <img
                          src={preview}
                          alt=""
                          className="w-full h-full object-cover pointer-events-none"
                        />
                        {index === 0 && !hasExistingImages && (
                          <Badge className="absolute top-1 left-1 text-[9px] px-1.5 py-0.5 h-auto bg-primary/90 pointer-events-none">
                            HERO
                          </Badge>
                        )}
                        <div className="absolute inset-x-0 bottom-0 flex gap-1 p-1.5 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                          <button
                            type="button"
                            aria-label={`Crop photo ${index + 1}`}
                            onClick={() => setEditingIndex(index)}
                            className="flex-1 flex items-center justify-center h-7 rounded-md bg-black/60 hover:bg-black/80 text-white/90 backdrop-blur-sm"
                          >
                            <Crop className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            aria-label={`Remove photo ${index + 1}`}
                            onClick={() => handleRemoveFile(index)}
                            className="flex items-center justify-center h-7 w-9 rounded-md bg-black/60 hover:bg-destructive/70 text-white/90 backdrop-blur-sm"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="aspect-square rounded-lg border border-dashed border-white/20 flex items-center justify-center hover:border-white/40 hover:bg-white/5 transition-colors"
                      aria-label="Add more photos"
                    >
                      <Camera className="w-5 h-5 text-muted-foreground" />
                    </button>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={handleClose}
                    >
                      Cancel
                    </Button>
                    <Button 
                      className="flex-1"
                      onClick={handleUpload}
                      disabled={isUploading}
                    >
                      {isUploading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        `Upload ${files.length} Photo${files.length > 1 ? 's' : ''}`
                      )}
                    </Button>
                  </div>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.heic,.heif,.dng"
                multiple
                className="hidden"
                onChange={(e) => {
                  if (e.target.files) handleFilesSelect(Array.from(e.target.files));
                  e.target.value = '';
                }}
              />
            </TabsContent>
            )}

            {/* Stock Images Tab */}
            <TabsContent
              value="stock"
              className={cn(
                'mt-4 space-y-4 flex-1 min-h-0 overflow-y-auto',
                stockOnly && 'max-sm:px-3 max-sm:pb-3 sm:mt-3 sm:pt-6'
              )}
            >
              {/* Search input — auto-searches as user types */}
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  {isLoadingStock ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4" />
                  )}
                </span>
                <SearchInput
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleStockSearch()}
                  placeholder="Search sample photos..."
                  className="h-10 pl-9 !bg-black/20"
                  maxLength={200}
                  autoFocus={stockOnly}
                />
              </div>
              
              {/* Rate Limit Warning */}
              {rateLimitInfo && (
                <div className="rounded-lg border border-caution/30 bg-caution/10 p-3 text-xs">
                  <div className="font-medium text-caution mb-1">⚠️ Rate Limited</div>
                  <p className="text-caution/80/80">
                    Stock image searches are limited to {rateLimitInfo.limit} per day.
                    Resets at: {new Date(rateLimitInfo.resetAt).toLocaleTimeString()}
                  </p>
                </div>
              )}

              {/* Results */}
              {isLoadingStock ? (
                <div className="grid grid-cols-2 gap-3">
                  {[...Array(4)].map((_, i) => (
                    <Skeleton key={i} className="aspect-square rounded-xl bg-white/[0.03]" />
                  ))}
                </div>
              ) : stockOptions.length > 0 ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    {stockOptions.map((option, index) => (
                      <button
                        key={index}
                        onClick={() => handleStockImageClick(option.imageUrl)}
                        disabled={isSavingStock}
                        className={cn(
                          "relative aspect-square rounded-xl overflow-hidden border-2 border-transparent",
                          "hover:border-primary/60 active:ring-2 active:ring-primary transition-all",
                          isSavingStock && "opacity-50 pointer-events-none"
                        )}
                      >
                        <img
                          src={option.imageUrl}
                          alt={`Stock option ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>

                  {hasMore && !stockOnly && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleLoadMore}
                      disabled={isLoadingMore}
                      className="w-full bg-white/[0.05] border-white/10 hover:bg-white/[0.08]"
                    >
                      {isLoadingMore ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        'Load More Options'
                      )}
                    </Button>
                  )}
                </div>
              ) : hasSearched ? (
                <div className="py-3">
                  <p className="text-xs text-muted-foreground text-center">
                    No images found. Try a different search.
                  </p>
                </div>
              ) : null}

              {!stockOnly && (
                <Button
                  variant="outline"
                  onClick={handleClose}
                  className="w-full bg-white/[0.05] border-white/10 hover:bg-white/[0.08]"
                >
                  Done
                </Button>
              )}
            </TabsContent>
          </Tabs>

          {stockOnly && hasMore && stockOptions.length > 0 && (
            <div
              className="shrink-0 px-3 pt-3"
              style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' }}
            >
              <Button
                variant="outline"
                onClick={handleLoadMore}
                disabled={isLoadingMore}
                className="w-full h-11 bg-white/[0.05] border-white/10 hover:bg-white/[0.08]"
              >
                {isLoadingMore ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Loading...
                  </>
                ) : (
                  'Load More Options'
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Crop editor for uploaded photos */}
      {editingIndex !== null && previews[editingIndex] && (
        <ItemImageEditorDialog
          open={true}
          onClose={() => setEditingIndex(null)}
          imageSrc={previews[editingIndex]}
          onSave={handleCropSave}
        />
      )}

      {/* Crop editor for stock photos */}
      {stockImageToEdit && (
        <ItemImageEditorDialog
          open={true}
          onClose={() => setStockImageToEdit(null)}
          imageSrc={stockImageToEdit}
          onSave={handleStockCropSave}
        />
      )}
    </>
  );
}
